// ============================================================================
// Notification Subscription Service
// ============================================================================
// Subscribes to the notifications table and shows toasts when new notifications
// arrive for the current user. This centralizes all toast display logic.
// ============================================================================

import { auth } from 'app/auth/auth.js';
import { globalSubscriptionManager } from 'app/state/subscription-manager.js';
import { toastManager } from 'shared/toast/toast.js';
import { getTypeConfig, getNavigationUrl, getNotificationLabel, getActorName, stripMentionMarkdown } from 'shared/notifications/notification-registry.js';

class NotificationSubscriptionService {
    constructor() {
        this.subscription = null;
        this.isInitialized = false;
        this._userId = null;
        // Toast coalescing: bursts (e.g. bulk assignment) collapse into one toast.
        this._toastBuffer = [];
        this._toastTimer = null;
    }

    /**
     * Initialize the service - call this after user is authenticated
     */
    async init() {
        if (this.isInitialized) return;

        const userId = auth.user?.id;
        if (!userId) return;

        this._userId = userId;
        this._setupSubscription(userId);
        this.isInitialized = true;
    }

    /**
     * Set up the realtime subscription for notifications table.
     * Delegates channel management and reconnection to globalSubscriptionManager.
     */
    _setupSubscription(userId) {
        this.subscription = globalSubscriptionManager.subscribe(
            'notifications',
            `user_id=eq.${userId}`,
            (payload) => {
                if (payload.eventType === 'INSERT') {
                    this.handleNewNotification(payload.new);
                }
            }
        );
    }

    /**
     * Handle a new notification - show toast and update UI
     * @param {Object} notification - The notification record from the database
     */
    handleNewNotification(notification) {
        if (!notification) return;

        // Dispatch immediately so the badge/panel stay accurate per-notification.
        // The full record is passed so the panel can prepend without a round-trip.
        window.dispatchEvent(new CustomEvent('notification-received', { detail: notification }));

        if (document.visibilityState !== 'visible') {
            // Background tab: native OS notification, no in-app toast.
            this._showBrowserNotification(notification);
            return;
        }

        // Focused tab: buffer briefly so a burst coalesces into a single toast.
        this._toastBuffer.push(notification);
        if (!this._toastTimer) {
            this._toastTimer = setTimeout(() => this._flushToasts(), 900);
        }
    }

    /**
     * Flush the toast buffer: one rich toast for a single notification, or one
     * summarizing toast for a burst.
     */
    _flushToasts() {
        this._toastTimer = null;
        const batch = this._toastBuffer;
        this._toastBuffer = [];
        if (!batch.length) return;

        if (batch.length === 1) {
            this.showToastForNotification(batch[0]);
            return;
        }

        const tally = new Map();
        for (const n of batch) tally.set(n.type, (tally.get(n.type) || 0) + 1);
        const entries = [...tally.entries()].sort((a, b) => b[1] - a[1]);
        const top = entries.slice(0, 3).map(([type, c]) => `${c} ${getNotificationLabel(type).toLowerCase()}`);
        const restCount = entries.slice(3).reduce((sum, [, c]) => sum + c, 0);

        toastManager.show({
            variant: 'notification',
            title: `${batch.length} new notifications`,
            subtitle: top.join(', ') + (restCount ? `, +${restCount} more` : ''),
            iconClass: 'fa-bell',
            dismissAfter: 7000,
            actionHint: 'Click to view',
            onClick: () => document.getElementById('notifications-bell')?.click()
        });
    }

    /**
     * Show a browser Notification API notification (only when tab is hidden)
     * @param {Object} notification - The notification record
     */
    _showBrowserNotification(notification) {
        if (!('Notification' in window) || Notification.permission !== 'granted') return;

        // Check user preference (stored in same localStorage as comment notification prefs)
        try {
            const stored = localStorage.getItem('commentNotificationPrefs');
            const prefs = stored ? JSON.parse(stored) : {};
            if (!prefs.browser) return;
        } catch { return; }

        const { title, message, type } = notification;
        new Notification(stripMentionMarkdown(title) || 'New notification', {
            body: stripMentionMarkdown(message) || '',
            icon: '/assets/aerium-icon-256.png',
            tag: `aerium-${type}-${Date.now()}`
        });
    }

    /**
     * Show the appropriate toast for a notification.
     * Extracts rich detail from notification data per type.
     * @param {Object} notification - The notification record
     */
    showToastForNotification(notification) {
        const { type, data = {}, title, message } = notification;

        const cfg = getTypeConfig(type);
        const variant = cfg.toastVariant;
        const url = getNavigationUrl(notification);
        const onClick = url ? () => { window.location.hash = url; } : null;

        const commentText = data.commentText || data.comment_text;
        const clientName = data.clientName || data.client_name;

        const opts = {
            variant,
            message: stripMentionMarkdown(title) || 'Notification',
            onClick,
            actionHint: onClick ? 'Click to view' : null
        };

        // Person-centric types (comment/mention) render an avatar from the actor name.
        if (cfg.initials) opts.avatarInitials = getActorName(notification) || undefined;

        // Type-specific enrichment
        if (type === 'comment') {
            opts.subtitle = commentText ? stripMentionMarkdown(commentText) : (message || undefined);
        } else if (type === 'mention') {
            opts.subtitle = commentText ? stripMentionMarkdown(commentText) : undefined;
        } else if (type === 'reaction') {
            opts.subtitle = commentText ? stripMentionMarkdown(commentText) : (message || undefined);
        } else if (type === 'title_unlocked') {
            const titleName = data.title_name || 'New Title';
            const titleIcon = data.title_icon || 'fa-trophy';
            opts.bodyHtml = `
                <div class="toast__message">${message || 'You earned a new title!'}</div>
                <div class="toast__title-badge"><i class="fas ${titleIcon}"></i> ${titleName}</div>
            `;
            opts.actionHint = 'Click to set it';
            // Dispatch event so title editor can refresh available titles
            window.dispatchEvent(new CustomEvent('title-unlocked', { detail: data }));
        } else {
            // Budget, leave, client, service, assignments - use notification.message
            opts.subtitle = message || undefined;
        }

        // Show task context (client · service) in footer when available
        if (clientName) {
            opts.footerLeft = data.serviceName
                ? `${clientName} \u00B7 ${data.serviceName}`
                : clientName;
        }

        toastManager.show(opts);
    }

    /**
     * Clean up - call this on logout
     */
    destroy() {
        this._userId = null;
        if (this.subscription) {
            this.subscription();
            this.subscription = null;
        }
        if (this._toastTimer) {
            clearTimeout(this._toastTimer);
            this._toastTimer = null;
        }
        this._toastBuffer = [];
        this.isInitialized = false;
    }
}

// Export singleton instance
export const notificationSubscriptionService = new NotificationSubscriptionService();
